import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import LocationSelector from './components/LocationSelector';
import MenuCategory from './components/MenuCategory';
import CartSidebar from './components/CartSidebar';
import OrderTracking from './components/OrderTracking';
import PaymentModal from './components/PaymentModal';

const OrderOnline = () => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [orderType, setOrderType] = useState('pickup'); // pickup or delivery
  const [selectedCategory, setSelectedCategory] = useState('signature-kebabs');
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [loyaltyPoints, setLoyaltyPoints] = useState(245);

  // Mock data for locations
  const locations = [
    {
      id: 1,
      name: "Downtown Palace",
      address: "123 Mediterranean Ave, Downtown",
      phone: "(555) 123-4567",
      estimatedTime: "15-20 min",
      deliveryZone: 3.5,
      coordinates: { lat: 40.7128, lng: -74.0060 }
    },
    {
      id: 2,
      name: "Westside Kebab",
      address: "456 Olive Street, Westside",
      phone: "(555) 234-5678",
      estimatedTime: "18-25 min",
      deliveryZone: 4.2,
      coordinates: { lat: 40.7589, lng: -73.9851 }
    }
  ];

  // Mock menu categories
  const menuCategories = [
    {
      id: 'signature-kebabs',
      name: 'Signature Kebabs',
      icon: 'ChefHat',
      description: 'Our famous grilled specialties',
      items: [
        {
          id: 1,
          name: "Palace Mixed Grill",
          description: "Lamb, chicken, and beef kebabs with rice pilaf and grilled vegetables",
          price: 24.99,
          image: "https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?w=400",
          spiceLevel: 2,
          prepTime: "20-25 min",
          popular: true,
          customizations: {
            spiceLevel: [1, 2, 3, 4, 5],
            sides: ["Rice Pilaf", "Bulgur", "Fries", "Salad"],
            extras: ["Extra Meat (+$5)", "Hummus (+$3)", "Pita Bread (+$2)"]
          }
        },
        {
          id: 2,
          name: "Lamb Shish Kebab",
          description: "Tender marinated lamb cubes with Mediterranean herbs",
          price: 19.99,
          image: "https://images.unsplash.com/photo-1544025162-d76694265947?w=400",
          spiceLevel: 3,
          prepTime: "18-22 min",
          customizations: {
            spiceLevel: [1, 2, 3, 4, 5],
            sides: ["Rice Pilaf", "Bulgur", "Fries", "Salad"],
            extras: ["Extra Meat (+$5)", "Hummus (+$3)", "Pita Bread (+$2)"]
          }
        }
      ]
    },
    {
      id: 'wraps-sandwiches',
      name: 'Wraps & Sandwiches',
      icon: 'Sandwich',
      description: 'Fresh wraps and hearty sandwiches',
      items: [
        {
          id: 3,
          name: "Chicken Shawarma Wrap",
          description: "Marinated chicken with garlic sauce, pickles, and fresh vegetables",
          price: 12.99,
          image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400",
          spiceLevel: 2,
          prepTime: "8-12 min",
          customizations: {
            spiceLevel: [1, 2, 3, 4],
            sauce: ["Garlic", "Tahini", "Hot Sauce", "Yogurt"],
            extras: ["Extra Chicken (+$3)", "Cheese (+$2)", "Avocado (+$2)"]
          }
        }
      ]
    },
    {
      id: 'appetizers',
      name: 'Appetizers',
      icon: 'Utensils',
      description: 'Perfect starters to share',
      items: [
        {
          id: 4,
          name: "Mediterranean Mezze Platter",
          description: "Hummus, baba ganoush, tabbouleh, olives, and pita bread",
          price: 16.99,
          image: "https://images.unsplash.com/photo-1571197119282-7c4e2b2e8c8b?w=400",
          spiceLevel: 1,
          prepTime: "5-8 min",
          vegetarian: true,
          customizations: {
            extras: ["Extra Pita (+$2)", "Feta Cheese (+$3)", "Olives (+$2)"]
          }
        }
      ]
    }
  ];

  const addToCart = (item, customizations = {}) => {
    const cartItem = {
      ...item,
      customizations,
      quantity: 1,
      cartId: Date.now() + Math.random()
    };
    setCart(prev => [...prev, cartItem]);
    setIsCartOpen(true);
  };

  const updateCartItem = (cartId, updates) => {
    setCart(prev => prev.map(item => 
      item.cartId === cartId ? { ...item, ...updates } : item
    ));
  };

  const removeFromCart = (cartId) => {
    setCart(prev => prev.filter(item => item.cartId !== cartId));
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      let itemPrice = item.price;
      if (item.customizations?.extras) {
        item.customizations.extras.forEach(extra => {
          if (extra.includes('+$')) {
            itemPrice += parseFloat(extra.match(/\+\$(\d+(?:\.\d{2})?)/)[1]);
          }
        });
      }
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  const handleCheckout = () => {
    if (cart.length === 0) return;
    setShowPayment(true);
  };

  const processOrder = (paymentDetails) => {
    const orderId = `KP${Date.now()}`;
    setOrderStatus({
      id: orderId,
      status: 'received',
      estimatedTime: selectedLocation?.estimatedTime || '15-20 min',
      items: cart,
      total: getCartTotal(),
      orderType,
      location: selectedLocation
    });
    setCart([]);
    setShowPayment(false);
    setIsCartOpen(false);
  };

  if (orderStatus) {
    return <OrderTracking orderStatus={orderStatus} onNewOrder={() => setOrderStatus(null)} />;
  }

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="font-headline text-4xl md:text-5xl font-bold mb-4">
              Order Fresh & Fast
            </h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              Authentic Mediterranean flavors delivered to your door or ready for pickup
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Location & Order Type Selection */}
            {!selectedLocation && (
              <LocationSelector
                locations={locations}
                onLocationSelect={setSelectedLocation}
                orderType={orderType}
                onOrderTypeChange={setOrderType}
              />
            )}

            {selectedLocation && (
              <>
                {/* Selected Location Summary */}
                <div className="card-surface flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <Icon name={orderType === 'pickup' ? 'MapPin' : 'Truck'} size={24} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-text-primary">{selectedLocation.name}</h3>
                      <p className="text-text-secondary text-sm">
                        {orderType === 'pickup' ? 'Pickup' : 'Delivery'} • {selectedLocation.estimatedTime}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedLocation(null)}
                    className="text-primary hover:text-primary-600 font-medium"
                  >
                    Change
                  </button>
                </div>

                {/* Loyalty Points Banner */}
                <div className="bg-accent-50 border border-accent-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Icon name="Star" size={24} className="text-accent-600 fill-current" />
                    <div>
                      <p className="font-semibold text-accent-800">You have {loyaltyPoints} points!</p>
                      <p className="text-accent-600 text-sm">Earn 1 point for every $1 spent</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-accent-800 font-semibold">$12.25 off</p>
                    <p className="text-accent-600 text-sm">available</p>
                  </div>
                </div>

                {/* Category Navigation */}
                <div className="flex space-x-2 overflow-x-auto pb-2">
                  {menuCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium whitespace-nowrap transition-organic ${
                        selectedCategory === category.id
                          ? 'bg-primary text-white shadow-warm'
                          : 'bg-surface text-text-primary hover:bg-primary-50'
                      }`}
                    >
                      <Icon name={category.icon} size={18} />
                      <span>{category.name}</span>
                    </button>
                  ))}
                </div>

                {/* Menu Items */}
                <MenuCategory
                  category={menuCategories.find(cat => cat.id === selectedCategory)}
                  onAddToCart={addToCart}
                />
              </>
            )}
          </div>

          {/* Cart Sidebar */}
          <div className="lg:col-span-1">
            <CartSidebar
              cart={cart}
              isOpen={isCartOpen}
              onToggle={() => setIsCartOpen(!isCartOpen)}
              onUpdateItem={updateCartItem}
              onRemoveItem={removeFromCart}
              onCheckout={handleCheckout}
              total={getCartTotal()}
              loyaltyPoints={loyaltyPoints}
            />
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPayment && (
        <PaymentModal
          cart={cart}
          total={getCartTotal()}
          orderType={orderType}
          location={selectedLocation}
          loyaltyPoints={loyaltyPoints}
          onClose={() => setShowPayment(false)}
          onProcessOrder={processOrder}
        />
      )}

      {/* Quick Reorder Section */}
      <div className="bg-surface py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-headline text-3xl font-bold text-text-primary mb-4">
              Quick Reorder
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Your favorite combinations, ready to order again
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: "Your Usual",
                items: "Palace Mixed Grill + Hummus",
                price: 27.99,
                lastOrdered: "3 days ago",
                image: "https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?w=300"
              },
              {
                name: "Quick Lunch",
                items: "Chicken Shawarma Wrap + Drink",
                price: 15.99,
                lastOrdered: "1 week ago",
                image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=300"
              },
              {
                name: "Family Feast",
                items: "Mixed Grill + Mezze Platter + 4 Wraps",
                price: 89.99,
                lastOrdered: "2 weeks ago",
                image: "https://images.unsplash.com/photo-1571197119282-7c4e2b2e8c8b?w=300"
              }
            ].map((order, index) => (
              <div key={index} className="bg-background rounded-lg p-6 shadow-warm hover:shadow-warm-lg transition-organic">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden">
                    <Image
                      src={order.image}
                      alt={order.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-text-primary">{order.name}</h3>
                    <p className="text-text-secondary text-sm">{order.items}</p>
                    <p className="text-text-secondary text-xs">Last ordered {order.lastOrdered}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-primary text-lg">${order.price}</span>
                  <button className="bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-600 transition-organic">
                    Reorder
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Group Ordering CTA */}
      <div className="bg-gradient-to-r from-conversion-500 to-primary-500 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Icon name="Users" size={48} className="mx-auto mb-6 opacity-90" />
          <h2 className="font-headline text-3xl font-bold mb-4">
            Ordering for the Office or Family?
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Start a group order and let everyone add their favorites to one shared cart
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-conversion-600 px-8 py-4 rounded-lg font-cta font-semibold hover:bg-gray-50 transition-organic">
              Start Group Order
            </button>
            <Link
              to="/catering-services-event-solutions"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-cta font-semibold hover:bg-white hover:text-conversion-600 transition-organic"
            >
              View Catering Options
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderOnline;